import { CSS_VARIABLES, DEFAULT_VALUES } from './constants';
import { formatDefaultValue, setCSSVarWithUnit } from './helpers';

/**
 * Sets CSS custom properties (variables) for dropzone positioning and styling
 * based on attribute data for different layout configurations (wide, left, right, full).
 *
 * @param {Object} attributeData - Object containing layout configuration data
 * @param {Object} attributeData.w - Wide layout configuration (skew, rotate, width, height, translations)
 * @param {Object} attributeData.l - Left layout configuration (skew, rotate, width, height, translations)
 * @param {Object} attributeData.r - Right layout configuration (skew, rotate, width, height, translations)
 * @param {Object} attributeData.f - Full layout configuration (transforms, dimensions, arrow positions)
 */
export const setDropzoneCSSVars = (attributeData) => {
  const root = document.documentElement;

  // Wide (core transforms + size)
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_SKEW_X,
    attributeData?.w?.sX,
    DEFAULT_VALUES.WIDE_SKEW_X.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_SKEW_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_ROTATE_Y,
    attributeData?.w?.rY,
    DEFAULT_VALUES.WIDE_ROTATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_ROTATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_ROTATE_X,
    attributeData?.w?.rX,
    DEFAULT_VALUES.WIDE_ROTATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_ROTATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_ROTATE_Z,
    attributeData?.w?.rZ,
    DEFAULT_VALUES.WIDE_ROTATE_Z.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_ROTATE_Z)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_WIDTH,
    attributeData?.w?.w,
    DEFAULT_VALUES.WIDE_WIDTH.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_WIDTH)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_HEIGHT,
    attributeData?.w?.h,
    DEFAULT_VALUES.WIDE_HEIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_HEIGHT)
  );

  // Left (core transforms + size)
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_LEFT_SKEW_X,
    attributeData?.l?.sX,
    DEFAULT_VALUES.LEFT_SKEW_X.unit,
    formatDefaultValue(DEFAULT_VALUES.LEFT_SKEW_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_LEFT_ROTATE_Y,
    attributeData?.l?.rY,
    DEFAULT_VALUES.LEFT_ROTATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.LEFT_ROTATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_LEFT_ROTATE_X,
    attributeData?.l?.rX,
    DEFAULT_VALUES.LEFT_ROTATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.LEFT_ROTATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_LEFT_ROTATE_Z,
    attributeData?.l?.rZ,
    DEFAULT_VALUES.LEFT_ROTATE_Z.unit,
    formatDefaultValue(DEFAULT_VALUES.LEFT_ROTATE_Z)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_LEFT_WIDTH,
    attributeData?.l?.w,
    DEFAULT_VALUES.LEFT_WIDTH.unit,
    formatDefaultValue(DEFAULT_VALUES.LEFT_WIDTH)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_LEFT_HEIGHT,
    attributeData?.l?.h,
    DEFAULT_VALUES.LEFT_HEIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.LEFT_HEIGHT)
  );

  // Right (core transforms + size)
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_RIGHT_SKEW_X,
    attributeData?.r?.sX,
    DEFAULT_VALUES.RIGHT_SKEW_X.unit,
    formatDefaultValue(DEFAULT_VALUES.RIGHT_SKEW_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_RIGHT_ROTATE_Y,
    attributeData?.r?.rY,
    DEFAULT_VALUES.RIGHT_ROTATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.RIGHT_ROTATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_RIGHT_ROTATE_X,
    attributeData?.r?.rX,
    DEFAULT_VALUES.RIGHT_ROTATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.RIGHT_ROTATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_RIGHT_ROTATE_Z,
    attributeData?.r?.rZ,
    DEFAULT_VALUES.RIGHT_ROTATE_Z.unit,
    formatDefaultValue(DEFAULT_VALUES.RIGHT_ROTATE_Z)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_RIGHT_WIDTH,
    attributeData?.r?.w,
    DEFAULT_VALUES.RIGHT_WIDTH.unit,
    formatDefaultValue(DEFAULT_VALUES.RIGHT_WIDTH)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_RIGHT_HEIGHT,
    attributeData?.r?.h,
    DEFAULT_VALUES.RIGHT_HEIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.RIGHT_HEIGHT)
  );

  // Wide front / rear translations
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_FRONT_TRANSLATE_X,
    attributeData?.w?.f?.tX,
    DEFAULT_VALUES.WIDE_FRONT_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_FRONT_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_FRONT_TRANSLATE_Y,
    attributeData?.w?.f?.tY,
    DEFAULT_VALUES.WIDE_FRONT_TRANSLATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_FRONT_TRANSLATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_REAR_TRANSLATE_X,
    attributeData?.w?.r?.tX,
    DEFAULT_VALUES.WIDE_REAR_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_REAR_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_REAR_TRANSLATE_Y,
    attributeData?.w?.r?.tY,
    DEFAULT_VALUES.WIDE_REAR_TRANSLATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_REAR_TRANSLATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_FRONT_ARROW_BOTTOM,
    attributeData?.w?.f?.a?.b,
    DEFAULT_VALUES.WIDE_FRONT_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_FRONT_ARROW_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_FRONT_ARROW_RIGHT,
    attributeData?.w?.f?.a?.r,
    DEFAULT_VALUES.WIDE_FRONT_ARROW_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_FRONT_ARROW_RIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_REAR_ARROW_BOTTOM,
    attributeData?.w?.r?.a?.b,
    DEFAULT_VALUES.WIDE_REAR_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_REAR_ARROW_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_WIDE_REAR_ARROW_RIGHT,
    attributeData?.w?.r?.a?.r,
    DEFAULT_VALUES.WIDE_REAR_ARROW_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.WIDE_REAR_ARROW_RIGHT)
  );

  // Front-left
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_LEFT_TRANSLATE_X,
    attributeData?.l?.f?.tX,
    DEFAULT_VALUES.FRONT_LEFT_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_LEFT_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_LEFT_TRANSLATE_Y,
    attributeData?.l?.f?.tY,
    DEFAULT_VALUES.FRONT_LEFT_TRANSLATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_LEFT_TRANSLATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_LEFT_ARROW_RIGHT,
    attributeData?.l?.f?.a?.r,
    DEFAULT_VALUES.FRONT_LEFT_ARROW_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_LEFT_ARROW_RIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_LEFT_ARROW_BOTTOM,
    attributeData?.l?.f?.a?.b,
    DEFAULT_VALUES.FRONT_LEFT_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_LEFT_ARROW_BOTTOM)
  );

  // Front-right
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_RIGHT_TRANSLATE_X,
    attributeData?.r?.f?.tX,
    DEFAULT_VALUES.FRONT_RIGHT_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_RIGHT_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_RIGHT_TRANSLATE_Y,
    attributeData?.r?.f?.tY,
    DEFAULT_VALUES.FRONT_RIGHT_TRANSLATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_RIGHT_TRANSLATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_RIGHT_ARROW_RIGHT,
    attributeData?.r?.f?.a?.r,
    DEFAULT_VALUES.FRONT_RIGHT_ARROW_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_RIGHT_ARROW_RIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FRONT_RIGHT_ARROW_BOTTOM,
    attributeData?.r?.f?.a?.b,
    DEFAULT_VALUES.FRONT_RIGHT_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.FRONT_RIGHT_ARROW_BOTTOM)
  );

  // Rear-left
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_LEFT_TRANSLATE_X,
    attributeData?.l?.r?.tX,
    DEFAULT_VALUES.REAR_LEFT_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_LEFT_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_LEFT_TRANSLATE_Y,
    attributeData?.l?.r?.tY,
    DEFAULT_VALUES.REAR_LEFT_TRANSLATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_LEFT_TRANSLATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_LEFT_ARROW_RIGHT,
    attributeData?.l?.r?.a?.r,
    DEFAULT_VALUES.REAR_LEFT_ARROW_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_LEFT_ARROW_RIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_LEFT_ARROW_BOTTOM,
    attributeData?.l?.r?.a?.b,
    DEFAULT_VALUES.REAR_LEFT_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_LEFT_ARROW_BOTTOM)
  );

  // Rear-right
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_RIGHT_TRANSLATE_X,
    attributeData?.r?.r?.tX,
    DEFAULT_VALUES.REAR_RIGHT_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_RIGHT_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_RIGHT_TRANSLATE_Y,
    attributeData?.r?.r?.tY,
    DEFAULT_VALUES.REAR_RIGHT_TRANSLATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_RIGHT_TRANSLATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_RIGHT_ARROW_RIGHT,
    attributeData?.r?.r?.a?.r,
    DEFAULT_VALUES.REAR_RIGHT_ARROW_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_RIGHT_ARROW_RIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_REAR_RIGHT_ARROW_BOTTOM,
    attributeData?.r?.r?.a?.b,
    DEFAULT_VALUES.REAR_RIGHT_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.REAR_RIGHT_ARROW_BOTTOM)
  );

  // Full
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_HEIGHT,
    attributeData?.f?.h,
    DEFAULT_VALUES.FULL_HEIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_HEIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_WIDTH,
    attributeData?.f?.w,
    DEFAULT_VALUES.FULL_WIDTH.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_WIDTH)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_SKEW_X,
    attributeData?.f?.sX,
    DEFAULT_VALUES.FULL_SKEW_X.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_SKEW_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_TRANSLATE_X,
    attributeData?.f?.tX,
    DEFAULT_VALUES.FULL_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_TRANSLATE_Y,
    attributeData?.f?.tY,
    DEFAULT_VALUES.FULL_TRANSLATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_TRANSLATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_ROTATE_Y,
    attributeData?.f?.rY,
    DEFAULT_VALUES.FULL_ROTATE_Y.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_ROTATE_Y)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_ROTATE_X,
    attributeData?.f?.rX,
    DEFAULT_VALUES.FULL_ROTATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_ROTATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_ROTATE_Z,
    attributeData?.f?.rZ,
    DEFAULT_VALUES.FULL_ROTATE_Z.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_ROTATE_Z)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_ARROW_RIGHT,
    attributeData?.f?.a?.r,
    DEFAULT_VALUES.FULL_ARROW_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_ARROW_RIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_ARROW_BOTTOM,
    attributeData?.f?.a?.b,
    DEFAULT_VALUES.FULL_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_ARROW_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.DROPZONE_FULL_ARROW_BOTTOM,
    attributeData?.f?.a?.b,
    DEFAULT_VALUES.FULL_ARROW_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.FULL_ARROW_BOTTOM)
  );
};

/**
 * Sets CSS custom properties (variables) for stack layout positioning and styling
 * based on attribute data for different stack configurations (rear, front, single, left/right groups).
 *
 * @param {Object} attributeData - Object containing stack layout configuration data
 * @param {Object} attributeData.r - Rear stack configuration (width, positioning, translations, gap)
 * @param {Object} attributeData.f - Front stack configuration (width, positioning, translations, gap)
 * @param {Object} attributeData.s - Single stack configuration (width, positioning, translations, gap)
 * @param {Object} attributeData.lt - Left group configuration (gap, margins, positioning)
 * @param {Object} attributeData.rt - Right group configuration (gap, margins, positioning)
 */
export const setStackLayoutCSSVars = (attributeData) => {
  const root = document.documentElement;

  // Rear
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_REAR_WIDTH,
    attributeData?.r?.w,
    DEFAULT_VALUES.STACKS_REAR_WIDTH.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_REAR_WIDTH)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_REAR_LEFT,
    attributeData?.r?.l,
    DEFAULT_VALUES.STACKS_REAR_LEFT.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_REAR_LEFT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_REAR_BOTTOM,
    attributeData?.r?.b,
    DEFAULT_VALUES.STACKS_REAR_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_REAR_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_REAR_TRANSLATE_X,
    attributeData?.r?.tX,
    DEFAULT_VALUES.STACKS_REAR_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_REAR_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_REAR_GAP,
    attributeData?.r?.g,
    DEFAULT_VALUES.STACKS_REAR_GAP.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_REAR_GAP)
  );

  // Front
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_FRONT_WIDTH,
    attributeData?.f?.w,
    DEFAULT_VALUES.STACKS_FRONT_WIDTH.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_FRONT_WIDTH)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_FRONT_LEFT,
    attributeData?.f?.l,
    DEFAULT_VALUES.STACKS_FRONT_LEFT.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_FRONT_LEFT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_FRONT_BOTTOM,
    attributeData?.f?.b,
    DEFAULT_VALUES.STACKS_FRONT_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_FRONT_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_FRONT_TRANSLATE_X,
    attributeData?.f?.tX,
    DEFAULT_VALUES.STACKS_FRONT_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_FRONT_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_FRONT_GAP,
    attributeData?.f?.g,
    DEFAULT_VALUES.STACKS_FRONT_GAP.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_FRONT_GAP)
  );

  // Single
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_SINGLE_WIDTH,
    attributeData?.s?.w,
    DEFAULT_VALUES.STACKS_SINGLE_WIDTH.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_SINGLE_WIDTH)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_SINGLE_LEFT,
    attributeData?.s?.l,
    DEFAULT_VALUES.STACKS_SINGLE_LEFT.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_SINGLE_LEFT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_SINGLE_BOTTOM,
    attributeData?.s?.b,
    DEFAULT_VALUES.STACKS_SINGLE_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_SINGLE_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_SINGLE_TRANSLATE_X,
    attributeData?.s?.tX,
    DEFAULT_VALUES.STACKS_SINGLE_TRANSLATE_X.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_SINGLE_TRANSLATE_X)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_SINGLE_GAP,
    attributeData?.s?.g,
    DEFAULT_VALUES.STACKS_SINGLE_GAP.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_SINGLE_GAP)
  );

  // Left group
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_LEFT_GAP,
    attributeData?.lt?.g,
    DEFAULT_VALUES.STACKS_LEFT_GAP.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_LEFT_GAP)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_LEFT_MARGIN_BOTTOM,
    attributeData?.lt?.mB,
    DEFAULT_VALUES.STACKS_LEFT_MARGIN_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_LEFT_MARGIN_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_LEFT_LEFT,
    attributeData?.lt?.l,
    DEFAULT_VALUES.STACKS_LEFT_LEFT.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_LEFT_LEFT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_LEFT_BOTTOM,
    attributeData?.lt?.b,
    DEFAULT_VALUES.STACKS_LEFT_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_LEFT_BOTTOM)
  );

  // Right group
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_RIGHT_GAP,
    attributeData?.rt?.g,
    DEFAULT_VALUES.STACKS_RIGHT_GAP.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_RIGHT_GAP)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_RIGHT_MARGIN_BOTTOM,
    attributeData?.rt?.mB,
    DEFAULT_VALUES.STACKS_RIGHT_MARGIN_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_RIGHT_MARGIN_BOTTOM)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_RIGHT_RIGHT,
    attributeData?.rt?.r,
    DEFAULT_VALUES.STACKS_RIGHT_RIGHT.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_RIGHT_RIGHT)
  );
  setCSSVarWithUnit(
    root,
    CSS_VARIABLES.STACKS_RIGHT_BOTTOM,
    attributeData?.rt?.b,
    DEFAULT_VALUES.STACKS_RIGHT_BOTTOM.unit,
    formatDefaultValue(DEFAULT_VALUES.STACKS_RIGHT_BOTTOM)
  );
};
